import { useState, useEffect, useCallback } from "react";

interface UseDescriptionTruncationProps {
	description?: string;
	hasVideo?: boolean;
	maxLength?: number;
}

interface UseDescriptionTruncationReturn {
	truncateDescription: boolean;
	showTruncateButton: boolean;
	toggleTruncateDescription: () => void;
}

const DEFAULT_MAX_LENGTH = 1600;

/**
 * Hook to manage description truncation logic
 * Only truncates if there's a video and description is longer than maxLength
 */
export const useDescriptionTruncation = ({
	description = "",
	hasVideo = false,
	maxLength = DEFAULT_MAX_LENGTH,
}: UseDescriptionTruncationProps): UseDescriptionTruncationReturn => {
	const [truncateDescription, setTruncateDescription] = useState(true);
	const [showTruncateButton, setShowTruncateButton] = useState(true);

	useEffect(() => {
		// Only truncate if there's a video and description is long enough
		const shouldTruncate = hasVideo && description.length > maxLength;

		if (shouldTruncate) {
			setTruncateDescription(true);
			setShowTruncateButton(true);
		} else {
			setTruncateDescription(false);
			setShowTruncateButton(false);
		}
	}, [description, hasVideo, maxLength]);

	const toggleTruncateDescription = useCallback(() => {
		setTruncateDescription((prev) => !prev);
	}, []);

	return {
		truncateDescription,
		showTruncateButton,
		toggleTruncateDescription,
	};
};

export type { UseDescriptionTruncationReturn };
